import { invoke } from "~/runtime"
import { useSignal } from "~/sdk/signal"
import Image from "~/components/ui/Image"
import type { JSX } from "react"
import type { Catalog } from "./Resale"
import type { Error } from "~/actions/resale"

interface Props {
    form: {
        title?: string
        text?: string
        success: {
            title?: string
            subtitle?: string
        }
    }
    catalogs: Catalog[]
}

export default function Form({ form, catalogs }: Props) {
    const result = useSignal<{
        success: boolean
        errors: Error[]
    }>({
        success: false,
        errors: [],
    })

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const form = event.currentTarget
        const formData = new FormData(form)
        const data = Object.fromEntries(formData)
        const { errors } = await invoke['site'].actions.resale(data)

        if (errors?.length) {
            result.value = { success: false, errors }
            return
        }

        result.value = { success: true, errors: [] }
        const catalogs = document.querySelector('#catalogs')
        catalogs?.classList.add('hidden')
    }

    const isInvalid = (inputName: string) =>
        result.value.errors.some((error) => error.field === inputName)

    const getErrorMessage = (inputName: string) =>
        result.value.errors.find((error) => error.field === inputName)?.message

    const removeErrorOnInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputName = event.currentTarget.name

        result.value = {
            ...result.value,
            errors: result.value.errors.filter((error) => error.field !== inputName),
        }
    }

    return (
        <div className='w-full'>
            {result.value.success
                ? (
                    <>
                        <h3 className='mb-8 text-white text-lg font-barlow italic lg:text-xl min-[1600px]:text-3xl'>
                            <strong className='resale-text-gradient text-2xl font-bold block lg:text-3xl'>
                                {form?.success?.title ?? 'Pronto!'}
                            </strong>{' '}
                            {form?.success?.subtitle ?? 'Comece a lucrar!'}
                        </h3>

                        <ul className='grid justify-center gap-10 md:grid-cols-2 lg:justify-between'>
                            {catalogs?.map((catalog, ci) => (
                                <li key={catalog.src ?? catalog.alt ?? ci} className='grid justify-items-center content-start gap-2'>
                                    <Image
                                        src={catalog.src}
                                        alt={catalog.alt}
                                        width={243}
                                        height={157}
                                        loading='lazy'
                                    />

                                    {catalog.options?.map((option, oi) => (
                                        <a
                                            key={option.url ?? option.name ?? oi}
                                            className={`w-full min-h-20 p-2 rounded-5 text-3xl text-center grid items-center ${
                                                option.highlight
                                                    ? 'bg-black text-[#ff89b4]'
                                                    : 'bg-white'
                                            }`}
                                            href={option.url}
                                            dangerouslySetInnerHTML={{ __html: option.name }}
                                        />
                                    ))}
                                </li>
                            ))}
                        </ul>
                    </>
                )
                : (
                    <div className='w-full lg:max-w-[460px]'>
                        {form?.title && (
                            <h3 className='mb-8 text-white text-lg font-barlow italic lg:text-xl min-[1600px]:text-3xl'>
                                <strong className='resale-text-gradient text-2xl font-bold block lg:text-3xl lg:inline min-[1600px]:text-[40px]'>
                                    {form.title}
                                </strong>{' '}
                                {form.text}
                            </h3>
                        )}

                        <form className='grid gap-4' onSubmit={onSubmit}>
                            <input
                                className='w-full h-11 pl-4 text-[#333333] text-sm placeholder:text-[#757575] min-[1600px]:h-14'
                                name='name'
                                type='text'
                                placeholder='Digite seu nome'
                                onChange={removeErrorOnInput}
                            />

                            {isInvalid('name') && (
                                <span className='-mt-2 mb-2 text-sm text-red-500 font-medium'>
                                    {getErrorMessage('name')}
                                </span>
                            )}

                            <input
                                className='w-full h-11 pl-4 text-[#333333] text-sm placeholder:text-[#757575] min-[1600px]:h-14'
                                name='email'
                                type='email'
                                placeholder='Digite seu e-mail'
                                onChange={removeErrorOnInput}
                            />

                            {isInvalid('email') && (
                                <span className='-mt-2 mb-2 text-sm text-red-500 font-medium'>
                                    {getErrorMessage('email')}
                                </span>
                            )}

                            <input
                                className='w-full h-11 pl-4 text-[#333333] text-sm placeholder:text-[#757575] min-[1600px]:h-14'
                                name='cellphone'
                                type='tel'
                                placeholder='Digite seu celular (apenas números)'
                                onChange={(e) => {
                                    e.currentTarget.value = e.currentTarget.value
                                        .replace(/\D/g, '')
                                        .replace(
                                            /^(\d?)(\d?)(\d{0,5})(\d{0,4})(.*)$/,
                                            (_all, $1, $2, $3, $4) => {
                                                let s = ''

                                                if ($1) s += `(${$1}${$2}`
                                                if ($3) s += `) ${$3}`
                                                if ($4) s += `-${$4}`

                                                return s
                                            },
                                        )
                                    removeErrorOnInput(e)
                                }}
                            />

                            {isInvalid('cellphone') && (
                                <span className='-mt-2 mb-2 text-sm text-red-500 font-medium'>
                                    {getErrorMessage('cellphone')}
                                </span>
                            )}

                            <button className='w-full h-[60px] rounded-[20px] bg-gradient-to-t from-[#ff9bc0] from-9% to-[#ff0074] to-130% text-white text-xl uppercase justify-self-center sm:max-w-xs lg:h-11 lg:justify-self-auto min-[1600px]:h-[60px] min-[1600px]:text-3xl min-[1600px]:mt-6'>
                                Baixar catálogo
                            </button>
                        </form>
                    </div>
                )}
        </div>
    )
}
